/**
 * Launch an Ace stability test — repeated runs on one case with one profile.
 */

import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import {
  agentReviewAPI,
  type AceProfileInfo,
  type AceStabilityTestRequest,
  type RunListItem,
} from "@/services/api/agentReview";
import type { AceProfile } from "./types";

export interface AceStabilityTestDialogProps {
  open: boolean;
  taskTypeLabel: string;
  reviewedCases: RunListItem[];
  running: boolean;
  error: string | null;
  onClose: () => void;
  onStart: (request: AceStabilityTestRequest) => void;
}

const ALL_PROFILES: AceProfile[] = ["strict", "balanced", "lenient"];

const FALLBACK_PROFILES: AceProfileInfo[] = [
  { id: "strict", label: "Strict", description: "Original Ace auditor", is_default: true },
  { id: "balanced", label: "Balanced", description: "Moderate strictness", is_default: false },
  { id: "lenient", label: "Lenient", description: "Benefit of the doubt", is_default: false },
];

function caseLabel(item: RunListItem): string {
  return item.subject_label?.trim() || item.label?.trim() || item.run_id.slice(-8);
}

export function AceStabilityTestDialog({
  open,
  taskTypeLabel,
  reviewedCases,
  running,
  error,
  onClose,
  onStart,
}: AceStabilityTestDialogProps) {
  const [aceProfiles, setAceProfiles] = useState<AceProfileInfo[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<AceProfile>("balanced");
  const [iterations, setIterations] = useState(10);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelectedProfile("balanced");
    setIterations(10);
    setSelectedRunId(reviewedCases[0]?.run_id ?? null);
  }, [open, reviewedCases]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setProfilesLoading(true);
    void agentReviewAPI
      .listAceProfiles()
      .then((rows) => {
        if (!cancelled) setAceProfiles(rows);
      })
      .catch(() => {
        if (!cancelled) setAceProfiles(FALLBACK_PROFILES);
      })
      .finally(() => {
        if (!cancelled) setProfilesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const profileRows = aceProfiles.length > 0 ? aceProfiles : FALLBACK_PROFILES;
  const canStart = Boolean(selectedRunId) && !running;

  const selectedCaseLabel = useMemo(() => {
    const item = reviewedCases.find((row) => row.run_id === selectedRunId);
    return item ? caseLabel(item) : null;
  }, [reviewedCases, selectedRunId]);

  const handleStart = () => {
    if (!selectedRunId) return;
    onStart({
      runId: selectedRunId,
      aceProfile: selectedProfile,
      iterations,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} data-testid="ace-stability-test-dialog">
      <DialogTitle>Ace stability test</DialogTitle>
      <DialogBody className="space-y-4">
        <p className="text-sm text-slate-300 leading-relaxed">
          <span className="text-slate-100">{taskTypeLabel}</span>
          {" — "}
          Run Ace repeatedly on one reviewed case with one profile to see which checks and phase
          RAGs stay consistent across runs.
        </p>

        <div className="space-y-2">
          <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
            Grading profile
          </p>
          {profilesLoading ? (
            <LoadingPlaceholder text="Loading profiles…" />
          ) : (
            profileRows.map((profile) => (
              <label
                key={profile.id}
                className={clsx(
                  "flex cursor-pointer gap-3 rounded-lg px-3 py-3 ring-1 transition-colors",
                  selectedProfile === profile.id
                    ? "bg-indigo-500/[0.08] ring-indigo-500/25"
                    : "bg-white/[0.02] ring-white/10 hover:bg-white/[0.04]",
                )}
              >
                <input
                  type="radio"
                  name="stability-profile"
                  checked={selectedProfile === profile.id}
                  onChange={() => setSelectedProfile(profile.id)}
                  className="mt-0.5 h-4 w-4 border-white/20 bg-slate-900 text-indigo-500 focus:ring-indigo-500/40"
                />
                <span className="min-w-0 space-y-0.5">
                  <span className="block text-sm font-medium text-slate-200">
                    {profile.label}
                    {profile.is_default ? " (original)" : ""}
                  </span>
                  <span className="block text-xs text-slate-500 leading-relaxed">
                    {profile.description}
                  </span>
                </span>
              </label>
            ))
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
              Iterations
            </p>
            <span className="text-sm tabular-nums text-slate-300">{iterations}</span>
          </div>
          <input
            type="range"
            min={10}
            max={100}
            step={1}
            value={iterations}
            onChange={(event) => setIterations(Number(event.target.value))}
            className="w-full accent-indigo-500"
          />
          <p className="text-xs text-slate-500">
            Between 10 and 100 sequential Ace runs on the selected case.
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
            Case to test
          </p>
          <div className="max-h-40 overflow-y-auto space-y-1 rounded-lg ring-1 ring-white/10 p-2">
            {reviewedCases.length === 0 ? (
              <p className="text-xs text-slate-500 px-2 py-1">No reviewed cases in this session.</p>
            ) : (
              reviewedCases.map((item) => (
                <label
                  key={item.run_id}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-white/[0.03]"
                >
                  <input
                    type="radio"
                    name="stability-case"
                    checked={selectedRunId === item.run_id}
                    onChange={() => setSelectedRunId(item.run_id)}
                    className="h-3.5 w-3.5 border-white/20 bg-slate-900 text-indigo-500"
                  />
                  <span className="text-sm text-slate-200 truncate">{caseLabel(item)}</span>
                </label>
              ))
            )}
          </div>
        </div>

        {iterations > 0 && selectedCaseLabel ? (
          <p className="text-xs text-amber-300/90">
            {iterations} sequential Ace run{iterations === 1 ? "" : "s"} on {selectedCaseLabel} —
            runs in the background; results appear below when complete.
          </p>
        ) : null}

        {error ? <p className="text-sm text-rose-400">{error}</p> : null}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleStart} disabled={!canStart} data-testid="ace-stability-test-start">
          {running ? "Starting…" : "Run in background"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
